import { Link } from 'react-router-dom';
import { Post } from '../../../../lib/supabase';
import { formatDate, calculateReadingTime } from '../../../../lib/utils';
import { categories } from '../../../../data/categories';

interface PostHeaderProps {
  post: Post;
}

export function PostHeader({ post }: PostHeaderProps) {
  const category = categories.find((c) => c.slug === post.category_slug);
  const readingTime = calculateReadingTime(post);

  return (
    <header className="mb-8 sm:mb-12">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-[#9A8E84] mb-6" aria-label="パンくずリスト">
        <Link to="/" className="hover:text-[#2C2416] transition-colors cursor-pointer" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
          Home
        </Link>
        <i className="ri-arrow-right-s-line text-[#C8BFB5]"></i>
        {category && (
          <>
            <Link
              to={`/categories/${post.category_slug}`}
              className="hover:text-[#2C2416] transition-colors cursor-pointer"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              {category.name}
            </Link>
            <i className="ri-arrow-right-s-line text-[#C8BFB5]"></i>
          </>
        )}
        <span className="text-[#7A6E62] line-clamp-1" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
          {post.title}
        </span>
      </nav>

      {/* Category label */}
      {category && (
        <div className="mb-4">
          <Link
            to={`/categories/${post.category_slug}`}
            className="inline-block text-xs font-semibold text-[#5B7B6F] tracking-wide hover:text-[#2C2416] transition-colors cursor-pointer"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            {category.name}
          </Link>
        </div>
      )}

      {/* Title */}
      <h1
        className="text-2xl sm:text-3xl font-bold text-[#2C2416] leading-snug mb-5"
        style={{ fontFamily: "'Noto Serif JP', 'Georgia', serif", letterSpacing: '-0.01em' }}
      >
        {post.title}
      </h1>

      {/* Excerpt / deck */}
      {post.excerpt && (
        <p
          className="text-base text-[#4A3F35] leading-relaxed mb-6 border-l-2 border-[#5B7B6F]/40 pl-4"
          style={{ fontFamily: "'Noto Serif JP', serif" }}
        >
          {post.excerpt}
        </p>
      )}

      {/* Meta row */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-[#9A8E84] pt-4 border-t border-[#EDE7DF]">
        <span className="flex items-center gap-1.5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
          <i className="ri-calendar-line"></i>
          {formatDate(post.published_at)}
        </span>
        {post.updated_at && post.updated_at !== post.published_at && (
          <span className="flex items-center gap-1.5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            <i className="ri-refresh-line"></i>
            更新: {formatDate(post.updated_at)}
          </span>
        )}
        <span className="flex items-center gap-1.5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
          <i className="ri-time-line"></i>
          約{readingTime}分
        </span>
        {post.author_name && (
          <span className="flex items-center gap-1.5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            <i className="ri-user-line"></i>
            {post.author_name}
          </span>
        )}
      </div>
    </header>
  );
}
